<!--
╔════════════════════════════════════════════════════════════════════╗
║  SectionThreePanels.vue - Sezione Tre Pannelli Espandibili        ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Componente hero fullscreen con 3 pannelli side-by-side che si espandono
al hover. Animazioni smooth con timing staggered. Mostra immagine,
titolo, descrizione, categorie e CTA solo quando espanso.

FEATURES:
  ✓ Tre pannelli in flex con espansione smooth
  ✓ Hover con delay 1 secondo prima dell'espansione
  ✓ Animazioni staggered per immagine e contenuto
  ✓ Categorie con badge styling
  ✓ CTA animato con arrow rotation
  ✓ Tracking mouse position durante hover
  ✓ Collapse automatico al mouseleave

LOGICA HOVER:
  1. MouseEnter: imposta hoveredPanel e timer di 1 sec
  2. Timer scade: attiva panel e avvia animazione (700ms)
  3. MouseLeave: cancella timer e deattiva panel
  
STRUTTURA PANNELLO:
  - eyebrow (sempre visibile)
  - titolo (sempre visibile, taglia al collapse)
  - immagine (nascosta fino all'espansione)
  - descrizione + testo (nascosti al collapse)
  - categorie (nascoste al collapse)
  - CTA (nascosto fino all'espansione)

CSS PROPERTIES:
  - is-animating: blocca overflow durante anim
  - has-expanded: applica margin ai pannelli
  - panel-expanded: flex 2.5
  - panel-collapsed: flex 0.75, testo opacity 0

DIPENDENZE: nessuna (pure Vue)
-->

<template>
  <section class="section-three-panels" :class="{ 'is-animating': isAnimating, 'has-expanded': activePanel }">
    
    <h2 v-if="content.title" class="section-main-title">{{ content.title }}</h2>

    <div class="panels-wrapper">
      
      <a 
        v-for="panel in content.panels" 
        :key="panel.id"
        :href="panel.link"
        :class="[
          'panel', 
          `panel-${panel.color}`,
          { 'panel-expanded': activePanel === panel.id },
          { 'panel-collapsed': activePanel && activePanel !== panel.id }
        ]"
        @mouseenter="handleMouseEnter(panel.id)"
        @mouseleave="handleMouseLeave"
      >

        
        
        <div class="panel-inner">
          
          <h2 class="panel-title">{{ panel.title }}</h2>

          <span class="panel-eyebrow">{{ panel.eyebrow }}</span>

          <div v-if="panel.image" class="panel-image-container">
            <img :src="panel.image" alt="" class="panel-image">
          </div>

          <div class="panel-main-content">
            
            <p class="panel-description">{{ panel.description }}</p>
            
            <p class="panel-text">{{ panel.text }}</p>

            <div v-if="panel.categories" class="panel-categories">
              <p class="categories-title">Le nostre soluzioni per:</p>
              <span v-for="(cat, idx) in panel.categories" :key="idx" class="category-badge">
                {{ cat }}
              </span>
            </div>

            <div class="panel-expanded-content">
              <p v-if="panel.additionalText" class="panel-additional-text">
                {{ panel.additionalText }}
              </p>
            </div>
          </div>

          <div class="panel-cta-wrapper">
            <span class="panel-cta">
              <span class="cta-label">{{ panel.ctaLabel }}</span>
              <span class="arrow">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </span>
          </div>
        </div>
      </a>
    </div>
  </section>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue'

// ──────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ──────────────────────────────────────────────────────────────
defineProps({
  /**
   * Contenuto della sezione
   * @type {Object}
   * @required
   * @property {string} title - Titolo sezione
   * @property {Array} panels - Array di pannelli
   *   @property {string} id - ID pannello univoco
   *   @property {string} color - Colore CSS class (panel-red, panel-blue, etc)
   *   @property {string} link - URL per navigazione click
   *   @property {string} eyebrow - Eyebrow testo
   *   @property {string} title - Titolo pannello
   *   @property {string} image - URL immagine
   *   @property {string} description - Descrizione breve
   *   @property {string} text - Testo aggiuntivo
   *   @property {Array} categories - Array categorie
   *   @property {string} additionalText - Testo espanso
   *   @property {string} ctaLabel - Label del CTA
   */
  content: {
    type: Object,
    required: true
  }
})

// ──────────────────────────────────────────────────────────────
// REACTIVE STATE
// ──────────────────────────────────────────────────────────────
/**
 * ID del pannello attualmente espanso
 * @type {string|null}
 */
const activePanel = ref(null)

/**
 * Flag animazione in corso (blocca overflow)
 * @type {boolean}
 */
const isAnimating = ref(false)

/**
 * ID del pannello su cui è il mouse
 * @type {string|null}
 */
const hoveredPanel = ref(null)

// Timer references
let animTimer = null
let hoverTimer = null

// ──────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ──────────────────────────────────────────────────────────────
onBeforeUnmount(() => {
  if (hoverTimer) clearTimeout(hoverTimer)
  if (animTimer) clearTimeout(animTimer)
})

// ──────────────────────────────────────────────────────────────
// METHODS
// ──────────────────────────────────────────────────────────────

/**
 * Avvia finestra di animazione (disabilita overflow)
 * @param {number} duration - Durata animazione in ms (default 700ms)
 */
function startAnimWindow(duration = 700) {
  if (animTimer) clearTimeout(animTimer)
  isAnimating.value = true
  animTimer = setTimeout(() => { isAnimating.value = false }, duration)
}

/**
 * Gestisce mouse enter su pannello
 * Imposta timer di 1 secondo prima di espandere
 * @param {string} panelId - ID pannello
 */
function handleMouseEnter(panelId) {
  // Disabilita logica hover su dispositivi touch o schermi piccoli (mobile/tablet)
  if (!window.matchMedia('(hover: hover)').matches || window.innerWidth <= 1024) return

  // Imposta pannello su cui è il mouse
  hoveredPanel.value = panelId
  
  // Cancella eventuali timer precedenti
  if (hoverTimer) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
  
  // Delay di 1 secondo prima di attivare
  hoverTimer = setTimeout(() => {
    // Verifica che il mouse sia ancora sullo stesso pannello
    if (hoveredPanel.value === panelId) {
      startAnimWindow(700)
      activePanel.value = panelId
    }
  }, 1000) // 1 secondo di ritardo
}

/**
 * Gestisce mouse leave da pannello
 * Cancella timer e deattiva pannello
 */
function handleMouseLeave() {
  // Disabilita logica hover su dispositivi touch o schermi piccoli (mobile/tablet)
  if (!window.matchMedia('(hover: hover)').matches || window.innerWidth <= 1024) return

  // Reset del pannello su cui è il mouse
  hoveredPanel.value = null
  
  // Cancella il timer se l'utente esce prima dello scadere
  if (hoverTimer) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
  
  // Se il pannello era attivo, lo deattiva
  if (activePanel.value) {
    startAnimWindow(700)
    activePanel.value = null
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

.section-three-panels {
  width: calc(100% + #{$space-4} * 2);
  margin-left: calc(-1 * #{$space-4});
  margin-right: calc(-1 * #{$space-4});
  padding: $space-2 0 0 0;
  background: $clr-white;
  overflow: visible;
  overflow-x: hidden;
  overflow-y: visible;

  @media (max-width: 600px) {
    width: calc(100% + #{$space-1} * 2);
    margin-left: calc(-1 * #{$space-1});
    margin-right: calc(-1 * #{$space-1});
  }

  .section-main-title {
    text-align: center;
    font-size: $font-title;
    color: $text-color;
    margin-bottom: $space-4;
    font-weight: 300;
    padding: 0 $space-2;
  }

  .panels-wrapper {
    display: flex;
    width: 100%;
    height: auto;
    position: relative;
    transition: padding 0.3s ease;
    box-sizing: border-box;
  }

  &.has-expanded .panels-wrapper {
    padding: 0 32px;
  }

  .panel {
    flex: 1;
    position: relative;
    display: flex;
    align-items: stretch;
    cursor: pointer;
    transition: flex 0.6s cubic-bezier(0.23, 1, 0.32, 1);
    overflow: hidden;
    text-decoration: none;
    color: inherit;

    &-separator {
      position: absolute;
      right: 0;
      top: 5%;
      bottom: 5%;
      width: 4px;
      background: $clr-brand-dark;
      opacity: 1;
      transition: none;
    }


    &.panel-expanded {
      flex: 2.5;

      .panel-separator { opacity: 1; }

      .panel-inner {
        padding: $space-2 32px;
      }

      .panel-eyebrow::before {
        width: 100%;
        transform: scaleY(1.3);
      }

      .panel-title {
        font-size: clamp(2.2rem, 4.5vw, 3.4rem);
      }

      .panel-cta-wrapper {
        opacity: 0;
        animation: fadeInSlide 0.4s ease 0.05s forwards;
      }

      // L'immagine viene gestita nella sezione &:not(.is-animating) .panel.panel-expanded
      // per assicurarsi che appaia solo quando l'animazione è completata

      // Desktop grande (>1400px): testo base è ~1.8rem, espanso diventa ~2.1rem
      .panel-description,
      .panel-text,
      .panel-additional-text {
        font-size: clamp(1.40rem, 2.6vw, 1.85rem); // ridotto da $font-lg (~2.1rem)
      }

      .panel-expanded-content {
        opacity: 0;
        animation: fadeInSlide 0.4s ease 0.05s forwards;
      }
    }

    &.panel-collapsed {
      flex: 0.75;

      .panel-title {
        font-size: clamp(1.4rem, 2.5vw, 2rem);
      }

      .panel-main-content {
        .panel-description,
        .panel-text,
        .panel-categories {
          opacity: 0;
          transition: opacity 0.25s ease;
        }
      }
    }

    &-inner {
      padding: $space-2;
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &-eyebrow {
      position: relative;
      display: inline-block;
      font-size: $font-eyebrow;
      font-weight: 600;
      font-family: 'Karla', sans-serif;
      text-transform: uppercase;
      letter-spacing: 1px;
      line-height: 1.2;
      margin: 0 0 $space-1 0;
      padding-left: 0;
      transition: all 1.2s ease;
      cursor: pointer;

      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 60px;
        height: 12px;
        opacity: 0.7;
        transition: all 1.5s ease;
        z-index: 2;
      }
    }

    &-title {
      font-size: clamp(1.3rem, 3vw, 2rem);
      font-weight: 300;
      font-family: 'Karla', sans-serif;
      color: $clr-brand-gray;
      line-height: 1.2;
      margin: 0 0 $space-2 $space-2;
      transition: all 1.5s cubic-bezier(0.23, 1, 0.32, 1);
      cursor: pointer;
    }

    &-image-container {
      width: 100%;
      height: 0;
      margin-top: -15px;
      margin-bottom: $space-2;
      overflow: hidden;
      opacity: 0;
      transform: translateY(-30px);
      transition: opacity 0.5s ease 0.3s, transform 0.5s ease 0.3s, height 0s ease 0s;
      display: flex;
      justify-content: center;
      cursor: pointer;
      z-index: 1;
      position: relative;

      .panel-image {
        width: 100%;
        height: auto;
        object-fit: cover;
        object-position: top;
        cursor: pointer;
      }
    }

    &-main-content {
      flex: 1;
      padding-left: $space-1;
      cursor: pointer;
    }

    &-description,
    &-text,
    &-additional-text {
      font-size: $font-base-lg;
      line-height: $line-height-base;
      color: $clr-brand-gray;
      margin: 0 0 $space-1-5 0;
      font-weight: 400;
      transition: none;
      cursor: pointer;
    }

    &-categories {
      margin: $space-1 0 $space-2 0;
      cursor: pointer;

      .categories-title {
        font-size: $font-base;
        color: $clr-text-light;
        margin: 0 0 $space-1 0;
        font-weight: 500;
        cursor: pointer;
      }

      .category-badge {
        display: inline-block;
        padding: $space-0-5 $space-1-5;
        background: rgba($clr-secondary, 0.1);
        color: $clr-brand-dark;
        font-size: $font-sm;
        font-weight: 600;
        letter-spacing: 0.5px;
        margin-right: $space-1;
        cursor: pointer;
      }
    }

    &-expanded-content {
      opacity: 0;
      transform: translateX(-20px);
      overflow: hidden;
      max-height: 0;
      transition: none;
      cursor: pointer;
    }

    // Gallery rimossa

    &-cta-wrapper {
      margin-top: auto;
      display: flex;
      justify-content: center;
      padding-top: $space-2;
      opacity: 0;
      transform: translateY(10px);
      transition: opacity 0.4s ease, transform 0.4s ease;
      cursor: pointer;
    }

    &-cta {
      font-size: clamp(0.85rem, 2.2vw, 1.3rem);
      color: $clr-brand-gray;
      text-decoration: none;
      font-weight: 600;
      transition: color 0.3s ease, transform 0.3s ease;
      display: inline-flex;
      align-items: center;
      @include flex-gap(0.35rem);
      cursor: pointer;

      @media (hover: hover) {
        &:hover {
          color: $clr-tertiary;
          transform: scale(1.05);

          .arrow {
            transform: rotate(90deg);
          }
        }
      }

      .cta-label {
        cursor: pointer;
      }

      .arrow {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1em;
        height: 1em;
        transition: transform 0.3s ease;
        transform: rotate(-90deg);
        cursor: pointer;

        svg {
          width: 100%;
          height: 100%;
          display: block;
        }
      }
    }

    &.panel-primary .panel-eyebrow { color: $clr-primary;   &::before { background-color: $clr-primary; } }
    &.panel-secondary .panel-eyebrow { color: $clr-secondary; &::before { background-color: $clr-secondary; } }
    &.panel-tertiary .panel-eyebrow { color: $clr-tertiary;   &::before { background-color: $clr-tertiary; } }
  }

  &.is-animating .panel {
    .panel-main-content {
      opacity: 0 !important;
      transition: opacity 0.15s ease !important;
    }

    .panel-image-container {
      opacity: 0 !important;
      transform: translateY(-30px) !important;
      transition: opacity 0.15s ease !important, transform 0.15s ease !important;
    }
  }

  &:not(.is-animating) .panel {
    .panel-main-content {
      opacity: 1;
      transition: opacity 0.3s ease 0.1s;
    }

    &.panel-expanded .panel-image-container {
      height: auto;
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.5s ease 0.3s, transform 0.5s ease 0.3s, height 0s ease 0s;
    }
  }

  @keyframes fadeInSlide {
    from { opacity: 0; transform: translateX(-20px); max-height: 0; }
    to   { opacity: 1; transform: translateX(0);     max-height: 800px; }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Desktop large (≤1600px)
  ------------------------------------------------------------------ */
  @media (max-width: 1600px) {
    .section-main-title {
      font-size: clamp(2.8rem, 4vw, 3.2rem);
    }

    .panel {
      &-inner {
        padding: clamp($space-1-5, 2vw, $space-2);
      }

      &.panel-expanded {
        .panel-description,
        .panel-text,
        .panel-additional-text {
          font-size: 1.75rem;
        }

        .panel-title {
          font-size: clamp(2rem, 4vw, 3.2rem);
        }
      }

      &-image-container .panel-image {
        height: auto;
      }

      &:not(.panel-expanded) .panel-image-container {
        height: 0;
      }

      &.panel-expanded .panel-image-container {
        height: auto !important;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Desktop medium (≤1400px)
  ------------------------------------------------------------------ */
  @media (max-width: 1400px) {
    .section-main-title {
      font-size: clamp(2.5rem, 3.8vw, 3rem);
    }

    .panel {
      &-inner {
        padding: clamp($space-1-5, 1.8vw, $space-2);
      }

      &.panel-expanded {
        .panel-description,
        .panel-text,
        .panel-additional-text {
          font-size: 1.55rem;
        }

        .panel-title {
          font-size: clamp(1.9rem, 3.8vw, 3rem);
        }
      }

      &-image-container .panel-image {
        height: auto;
      }

      &:not(.panel-expanded) .panel-image-container {
        height: 0;
      }

      &.panel-expanded .panel-image-container {
        height: auto !important;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Laptop (≤1200px)
  ------------------------------------------------------------------ */
  @media (max-width: 1200px) {
    .section-main-title {
      font-size: clamp(2.2rem, 3.5vw, 2.8rem);
    }

    .panel {
      &-inner {
        padding: clamp($space-1-5, 1.6vw, $space-2);
      }

      &.panel-expanded {
        .panel-description,
        .panel-text,
        .panel-additional-text {
          font-size: 1.3rem;
        }

        .panel-title {
          font-size: clamp(1.8rem, 3.5vw, 2.8rem);
        }
      }

      &-image-container .panel-image {
        height: auto;
      }

      &:not(.panel-expanded) .panel-image-container {
        height: 0;
      }

      &.panel-expanded .panel-image-container {
        height: auto !important;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Laptop small (≤1100px)
  ------------------------------------------------------------------ */
  @media (max-width: 1100px) {
    .section-main-title {
      font-size: clamp(2rem, 3.2vw, 2.6rem);
    }

    .panel {
      &.panel-expanded {
        .panel-description,
        .panel-text,
        .panel-additional-text {
          font-size: 1.2rem;
        }

        .panel-title {
          font-size: clamp(1.7rem, 3.2vw, 2.6rem);
        }
      }

      &-image-container .panel-image {
        height: auto;
      }

      &:not(.panel-expanded) .panel-image-container {
        height: 0;
      }

      &.panel-expanded .panel-image-container {
        height: auto !important;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Tablet (≤1024px)
  ------------------------------------------------------------------ */
  @media (max-width: 1024px) {
    min-height: auto;
    padding: $space-3 0;

    .panels-wrapper {
      flex-direction: column;
      height: auto;
      @include flex-gap($space-2, column);
    }

    .panel {
      flex: initial !important;
      min-height: auto;
      cursor: default; // Rimuove il cursore pointer
      pointer-events: none; // Disabilita tutti gli eventi hover/click sul pannello

      &-inner {
        padding: $space-2;
        pointer-events: auto; // Riabilita gli eventi sui contenuti interni (es. link)
      }

      // Mostra sempre l'immagine (non più condizionata all'hover)
      &-image-container {
        height: 280px !important; // Sempre visibile
        opacity: 1 !important;
        transform: translateY(0) !important;
        transition: none !important;

        .panel-image {
          height: 280px;
        }
      }

      // Rimuove tutti gli effetti hover
      @media (hover: hover) {
        &:hover {
          .panel-cta-wrapper {
            opacity: 1;
            transform: translateY(0);
          }
        }
      }

      &-cta-wrapper {
        opacity: 1 !important; // Sempre visibile
        transform: translateY(0) !important;
        transition: none !important;
      }

      // Tutti i pannelli sono sempre "espansi" in modalità colonna
      &-main-content {
        opacity: 1 !important;
        transform: translateX(0) !important;
      }

      &-expanded-content {
        animation: none !important;
        opacity: 1 !important;
        max-height: none !important;
        transform: translateX(0) !important;
      }

      // Testo base per tablet
      &-description,
      &-text {
        font-size: $font-base-sm;
      }

      &-title {
        font-size: clamp(1.6rem, 3.5vw, 2.2rem);
      }

      // Tablet: tutti i testi hanno la stessa dimensione
      &-description,
      &-text,
      &-additional-text {
        font-size: 1.15rem;
      }

      // Disabilita le classi di espansione/collasso
      &.panel-expanded,
      &.panel-collapsed {
        flex: initial !important;

        .panel-main-content {
          opacity: 1 !important;
        }
      }
    }

    // Disabilita l'animazione di transizione
    &.is-animating .panel {
      .panel-main-content,
      .panel-image-container {
        opacity: 1 !important;
        transform: translateY(0) !important;
        transition: none !important;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Tablet small (≤900px)
  ------------------------------------------------------------------ */
  @media (max-width: 900px) {
    .section-main-title {
      font-size: clamp(1.9rem, 3vw, 2.4rem);
      margin-bottom: $space-3;
    }

    .panel {
      &-inner {
        padding: $space-1-5;
      }

      &-title {
        font-size: clamp(1.5rem, 3.2vw, 2rem);
      }

      &-image-container {
        height: 260px !important;

        .panel-image {
          height: 260px;
        }
      }

      &-description,
      &-text,
      &-additional-text {
        font-size: 1.1rem;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Mobile (≤768px)
  ------------------------------------------------------------------ */
  @media (max-width: 768px) {
    padding-top: $space-2;
    padding-bottom: $space-2;

    .section-main-title {
      font-size: $font-title-sm;
      margin-bottom: $space-1-5;
    }

    .panel {
      min-height: auto;

      &-inner {
        padding: 0;
      }

      &-title {
        font-size: clamp(1.2rem, 4.5vw, 1.8rem);
      }

      &-image-container {
        height: 240px !important;
        opacity: 1 !important;
        transform: translateY(0) !important;

        .panel-image {
          height: 240px;
        }
      }

      // Tutti i testi hanno la stessa dimensione
      &-description,
      &-text,
      &-additional-text {
        font-size: $font-base-sm;
        font-weight: 400;
      }

      &-categories .category-badge {
        font-size: $font-xs;
        padding: $space-0-25 $space-0-75;
      }

      &-cta-wrapper {
        padding-top: $space-1;
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Mobile medium (≤600px)
  ------------------------------------------------------------------ */
  @media (max-width: 600px) {
    .section-main-title {
      font-size: clamp(1.6rem, 4.5vw, 2rem);
    }

    .panel {
      &-inner {
        padding: 0;
      }

      &-title {
        font-size: clamp(1.15rem, 4.2vw, 1.7rem);
      }

      &-image-container {
        height: 220px !important;

        .panel-image {
          height: 220px;
        }
      }

      &-description,
      &-text,
      &-additional-text {
        font-size: clamp(0.95rem, 3vw, 1.1rem);
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Mobile piccolo (≤480px)
  ------------------------------------------------------------------ */
  @media (max-width: 480px) {
    .section-main-title { font-size: $font-title-xs; }

    .panel {
      &-image-container {
        width: 100vw; // Larghezza piena dello schermo
        position: relative;
        left: 50%;
        right: 50%;
        margin-left: -50vw;
        margin-right: -50vw;
        height: 200px !important; // Altezza ridotta per mobile piccolo

        .panel-image {
          width: 100%;
          height: 200px;
          object-fit: cover; // Cambiato da contain a cover per riempire tutto lo spazio
        }
      }
    }

    .panel {
      &-inner { padding: 0; }

      &-eyebrow { font-size: $font-eyebrow-xs; &::before { height: 8px; left: -15px; } }

      &-title { font-size: clamp(1.1rem, 5.5vw, 1.6rem); }

      // Tutti i testi hanno la stessa dimensione
      &-description,
      &-text,
      &-additional-text {
        font-size: $font-base-xs;
      }

      &.panel-expanded {
        .panel-title {
          font-size: clamp(1.3rem, 5.5vw, 1.8rem);
        }

        // Tutti i testi hanno la stessa dimensione quando espanso
        .panel-description,
        .panel-text,
        .panel-additional-text {
          font-size: calc(#{$font-base-xs} * 1.17);
        }
      }
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Mobile molto piccolo (≤360px)
  ------------------------------------------------------------------ */
  @media (max-width: 360px) {
    .panel {
      // Tutti i testi hanno la stessa dimensione
      &-description,
      &-text,
      &-additional-text {
        font-size: $font-base-xxs;
      }

      &.panel-expanded {
        // Tutti i testi hanno la stessa dimensione quando espanso
        .panel-description,
        .panel-text,
        .panel-additional-text {
          font-size: calc(#{$font-base-xxs} * 1.17);
        }
      }
    }
  }
}
</style>